import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { Translation } from '@/hoslog-intl';
import { GlobeSolidIcon } from '@/icons/globe-solid';
import { formatDate } from '@/lib/dateFormatter';
import { parseUserAgent } from '@/lib/parseUserAgent';
import { ActivityLog } from '@/services/dashboard/conversionAnalytics/conversionAnalytics.types';
import type { ColumnDef } from '@tanstack/react-table';
import { Phone } from 'lucide-react';

const textCell = (value: string | number | null | undefined) => (
  <div className="font-medium text-xs text-foreground-detail whitespace-nowrap">{value || '-'}</div>
);

const sourcePageTranslationIds: Record<string, string> = {
  'Store Page': 'dashboard.conversion.page.storePage',
  'Host Detail Page': 'dashboard.conversion.page.hostPage',
};

const sourcePageCell = (sourcePage: string, clubCode?: string, hostCode?: string) => {
  const translationId = sourcePageTranslationIds[sourcePage];
  const sourceCode = sourcePage === 'Store Page' ? clubCode : sourcePage === 'Host Detail Page' ? hostCode : undefined;

  return (
    <div className="flex flex-col gap-1 whitespace-nowrap text-xs font-medium text-foreground-detail">
      <span>{translationId ? <Translation id={translationId} /> : sourcePage || '-'}</span>
      {sourceCode ? <span className="text-foreground-subtle">ID: {sourceCode}</span> : null}
    </div>
  );
};

const targetNameCell = (targetName: string, locationName?: string) => (
  <div className="whitespace-pre text-xs font-medium text-foreground-detail">
    {targetName}
    {locationName ? `  ${locationName}` : null}
  </div>
);

const userAgentCell = (userAgent: string) => {
  const { name, version, platform } = parseUserAgent(userAgent);

  return (
    <div className="whitespace-nowrap text-xs font-medium text-foreground-detail">
      Name = {name}, Version = {version}, Platform = {platform}
    </div>
  );
};

const actionTypeCell = (value: string | null | undefined, translatedValue: string) => {
  const actionType = value?.toLowerCase();

  if (actionType === 'phone') {
    return (
      <div className="flex items-center gap-2 whitespace-nowrap text-sm font-medium text-success">
        <Phone className="size-4" />
        <span>{translatedValue}</span>
      </div>
    );
  }

  if (actionType === 'reservation') {
    return (
      <div className="flex items-center gap-2 whitespace-nowrap text-sm font-medium text-warning">
        <GlobeSolidIcon className="size-4" />
        <span>{translatedValue}</span>
      </div>
    );
  }

  return textCell(translatedValue);
};

const userStatusCell = (status: string, activityId: string, t: (key: string) => string) => {
  const normalizedStatus = status.toLowerCase();
  const isGuest = normalizedStatus === 'guest';
  const isMember = normalizedStatus === 'member';
  const label = isGuest
    ? t('dashboard.conversion.page.guest')
    : isMember
      ? t('dashboard.conversion.page.member')
      : status;
  const statusClassName = isGuest
    ? 'text-sm font-medium text-status-info'
    : isMember
      ? 'text-sm font-medium text-success'
      : 'text-sm font-medium text-foreground-detail';

  return (
    <div className="flex flex-col gap-1 whitespace-nowrap">
      <span className={statusClassName}>{label}</span>
      <span className="text-xs text-foreground-caption">{activityId || '-'}</span>
    </div>
  );
};

export function getColumns(t: (key: string) => string): ColumnDef<ActivityLog>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => textCell(getSerialNumber(table, row.index)),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'actionType',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.actionType')} />,
      cell: ({ row }) => {
        const actionType: string = row.getValue('actionType');
        const textToRender =
          actionType.toLowerCase() == 'phone'
            ? t('dashboard.conversion.page.phone')
            : t('dashboard.conversion.page.reservation');
        return actionTypeCell(actionType, textToRender);
      },
    },
    {
      accessorKey: 'sourcePage',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sourcePage')} />,
      cell: ({ row }) =>
        sourcePageCell(row.original.sourcePage, row.original.clubCode, row.original.hostCode ?? undefined),
    },
    {
      accessorKey: 'targetName',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.targetName')} />,
      cell: ({ row }) =>
        targetNameCell(
          row.original.targetName,
          row.original.sourcePage === 'Store Page' ? row.original.prefecture : undefined,
        ),
    },
    {
      accessorKey: 'userStatus',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.userStatus')} />,
      cell: ({ row }) => userStatusCell(row.original.userStatus, row.original.activityId, t),
    },
    {
      accessorKey: 'prefecture',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('dashboard.conversion.page.prefecture')} />
      ),
      cell: ({ row }) => textCell(row.original.customerLocationJson.cityName),
    },
    {
      accessorKey: 'userAgent',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.browser')} />,
      cell: ({ row }) => userAgentCell(row.original.userAgent ?? ''),
    },
    {
      id: 'createdDate',
      accessorFn: (row) => row.dateTime,
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.dateTime')} />,
      cell: ({ row }) => textCell(formatDate(Number(row.getValue('createdDate')), 'YYYY/MM/DD HH:mm')),
      meta: { label: 'Date/Time' },
      enableColumnFilter: true,
    },
  ];
}
